'use client'

import React, {useId, useMemo} from 'react'
import {cn} from './cn'

interface PaginationLabels {
    navigation: string
    previous: string
    next: string
    perPage: string
    showing: string
    of: string
    resultsOne: string
    resultsFew: string
    resultsMany: string
    pageButton: string
    currentPageButton: string
    ellipsis: string
}

export interface PaginationProps {
    locale: string
    page: number
    pageSize: number
    total: number
    onPageChange: (page: number) => void
    onPageSizeChange?: (pageSize: number) => void
    pageSizeOptions?: number[]
    labels?: Partial<PaginationLabels>
    formatSummary?: (params: {start: number, end: number, total: number}) => string
    loading?: boolean
    className?: string
}

const DEFAULT_PAGINATION_LABELS_PL: PaginationLabels = {
    navigation: 'Paginacja',
    previous: 'Poprzednia',
    next: 'Następna',
    perPage: 'Na stronę:',
    showing: 'Pokazuję',
    of: 'z',
    resultsFew: 'wyniki',
    resultsOne: 'wynik',
    resultsMany: 'wyników',
    pageButton: 'Przejdź do strony {page}',
    currentPageButton: 'Bieżąca strona {page}',
    ellipsis: 'Więcej stron',
}

const DEFAULT_PAGINATION_LABELS_EN: PaginationLabels = {
    navigation: 'Pagination',
    previous: 'Previous',
    next: 'Next',
    perPage: 'Per page:',
    showing: 'Showing',
    of: 'of',
    resultsFew: 'results',
    resultsOne: 'result',
    resultsMany: 'results',
    pageButton: 'Go to page {page}',
    currentPageButton: 'Current page {page}',
    ellipsis: 'More pages',
}

function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}, (_, i) => i + 1)
    }

    const pages: (number | string)[] = []

    if (currentPage <= 3) {
        for (let i = 1; i <= 4; i += 1) {
            pages.push(i)
        }
        pages.push('ellipsis')
        pages.push(totalPages)
    } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = totalPages - 3; i <= totalPages; i += 1) {
            pages.push(i)
        }
    } else {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
            pages.push(i)
        }
        pages.push('ellipsis')
        pages.push(totalPages)
    }

    return pages
}

const selectBaseClasses =
    'rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm text-gray-700 dark:text-gray-300 focus:border-primary-500 focus:outline-hidden focus:ring-1 focus:ring-primary-500 shadow-sm hover:border-gray-400 dark:hover:border-gray-500 transition-colors appearance-none bg-no-repeat bg-right bg-[length:12px] pr-8'

export const Pagination: React.FC<PaginationProps> = ({
    locale,
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [25, 50, 100],
    labels,
    formatSummary,
    loading = false,
    className
}) => {
    const defaultLabels = locale === 'pl' ? DEFAULT_PAGINATION_LABELS_PL : DEFAULT_PAGINATION_LABELS_EN
    const mergedLabels: PaginationLabels = {
        ...defaultLabels,
        ...labels
    }
    const normalizedPageSizeOptions = pageSizeOptions
        .filter((option) => Number.isFinite(option) && option > 0)
        .map((option) => Math.floor(option))
    const safePageSizeOptions = normalizedPageSizeOptions.length > 0 ? normalizedPageSizeOptions : [25, 50, 100]
    const safeTotal = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0
    const rawPageSize = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : safePageSizeOptions[0]
    const safePageSize = safePageSizeOptions.includes(rawPageSize) ? rawPageSize : safePageSizeOptions[0]
    const computedTotalPages = Math.max(1, Math.ceil(safeTotal / safePageSize))
    const safePageUnclamped = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1
    const safePage = Math.min(Math.max(1, safePageUnclamped), computedTotalPages)

    const totalPages = computedTotalPages
    const hasPrevious = safePage > 1
    const hasNext = safePage < totalPages
    const startItem = safeTotal > 0 ? (safePage - 1) * safePageSize + 1 : 0
    const endItem = safeTotal > 0 ? Math.min(safePage * safePageSize, safeTotal) : 0
    const pageSizeSelectId = useId()

    const pageNumbers = useMemo(() => generatePageNumbers(safePage, totalPages), [safePage, totalPages])
    const pluralRules = useMemo(
        () => new Intl.PluralRules(locale),
        [locale]
    )
    const resultsLabel = useMemo(() => {
        const category = pluralRules.select(safeTotal)
        if (category === 'one') {
            return mergedLabels.resultsOne
        }
        if (category === 'few') {
            return mergedLabels.resultsFew
        }
        return mergedLabels.resultsMany
    }, [mergedLabels.resultsFew, mergedLabels.resultsMany, mergedLabels.resultsOne, pluralRules, safeTotal])
    const summaryText = formatSummary
        ? formatSummary({start: startItem, end: endItem, total: safeTotal})
        : null
    const fallbackSummaryText = `${mergedLabels.showing} ${startItem}–${endItem} ${mergedLabels.of} ${safeTotal} ${resultsLabel}`
    const displayedSummaryText = summaryText ?? fallbackSummaryText

    const formatPageLabel = (template: string, pageNumber: number) => template.replace('{page}', String(pageNumber))

    const handlePrevious = () => {
        if (hasPrevious && !loading) {
            onPageChange(safePage - 1)
        }
    }

    const handleNext = () => {
        if (hasNext && !loading) {
            onPageChange(safePage + 1)
        }
    }

    const handlePageClick = (targetPage: number) => {
        if (!loading && targetPage !== safePage && targetPage >= 1 && targetPage <= totalPages) {
            onPageChange(targetPage)
        }
    }

    if (safeTotal === 0) {
        return null
    }

    return (
        <div className={cn('bg-white dark:bg-gray-800', className)}>
            <div className="flex items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3">
                <div className="flex w-full flex-col gap-3 sm:hidden">
                    <p className="text-sm text-gray-500 dark:text-gray-400" aria-live="polite">
                        {displayedSummaryText}
                    </p>
                    <div className="flex items-center gap-3">
                        <nav aria-label={mergedLabels.navigation} className="isolate inline-flex -space-x-px rounded-xl shadow-sm">
                            <button
                                type="button"
                                onClick={handlePrevious}
                                disabled={!hasPrevious}
                                aria-disabled={!hasPrevious || loading}
                                className={cn(
                                    'btn-micro relative inline-flex h-11 w-11 !min-h-[44px] items-center justify-center rounded-l-xl px-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:z-20 focus:outline-offset-0',
                                    hasPrevious && !loading
                                        ? 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                        : 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <span className="sr-only">{mergedLabels.previous}</span>
                                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-5 w-5">
                                    <path
                                        d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                                        clipRule="evenodd"
                                        fillRule="evenodd"
                                    />
                                </svg>
                            </button>

                            <button
                                type="button"
                                disabled
                                aria-current="page"
                                aria-label={formatPageLabel(mergedLabels.currentPageButton, safePage)}
                                className="btn-micro z-10 relative inline-flex h-11 w-14 !min-h-[44px] items-center justify-center bg-primary-600 px-4 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                            >
                                {safePage}
                            </button>

                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!hasNext}
                                aria-disabled={!hasNext || loading}
                                className={cn(
                                    'btn-micro relative inline-flex h-11 w-11 !min-h-[44px] items-center justify-center rounded-r-xl px-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:z-20 focus:outline-offset-0',
                                    hasNext && !loading
                                        ? 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                        : 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <span className="sr-only">{mergedLabels.next}</span>
                                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-5 w-5">
                                    <path
                                        d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                                        clipRule="evenodd"
                                        fillRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </nav>

                        {onPageSizeChange && (
                            <select
                                value={safePageSize}
                                onChange={(event) => onPageSizeChange(Number(event.target.value))}
                                disabled={loading}
                                aria-label={mergedLabels.perPage}
                                className={cn(
                                    selectBaseClasses,
                                    'h-11 min-w-[76px] shrink-0 rounded-xl !py-0 text-base font-medium'
                                )}
                                style={{
                                    backgroundImage:
                                        "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                                    backgroundPosition: 'right 8px center'
                                }}
                            >
                                {safePageSizeOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between sm:gap-6">
                    <div className="min-w-0">
                        <p className="text-sm text-gray-700 dark:text-gray-300" aria-live="polite">
                            {displayedSummaryText}
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {onPageSizeChange && (
                            <div className="flex items-center gap-2">
                                <label htmlFor={pageSizeSelectId} className="text-sm text-gray-700 dark:text-gray-300">
                                    {mergedLabels.perPage}
                                </label>
                                <select
                                    id={pageSizeSelectId}
                                    data-testid="desktop-page-size-select"
                                    value={safePageSize}
                                    onChange={(event) => onPageSizeChange(Number(event.target.value))}
                                    disabled={loading}
                                    className={cn(selectBaseClasses, 'h-11 min-w-[76px] !py-0 text-base font-medium')}
                                    style={{
                                        backgroundImage:
                                            "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                                        backgroundPosition: 'right 8px center'
                                    }}
                                >
                                    {safePageSizeOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <nav aria-label={mergedLabels.navigation} className="isolate inline-flex -space-x-px rounded-xl shadow-sm">
                            <button
                                type="button"
                                onClick={handlePrevious}
                                disabled={!hasPrevious}
                                aria-disabled={!hasPrevious || loading}
                                className={cn(
                                    'btn-micro relative inline-flex h-11 w-11 !min-h-[44px] items-center justify-center rounded-l-xl px-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:z-20 focus:outline-offset-0',
                                    hasPrevious && !loading
                                        ? 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                        : 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <span className="sr-only">{mergedLabels.previous}</span>
                                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-5 w-5">
                                    <path
                                        d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                                        clipRule="evenodd"
                                        fillRule="evenodd"
                                    />
                                </svg>
                            </button>

                            {pageNumbers.map((pageNum, index) => {
                                if (typeof pageNum === 'string') {
                                    return (
                                        <span
                                            key={`ellipsis-${index}`}
                                            aria-hidden="true"
                                            title={mergedLabels.ellipsis}
                                            className="relative inline-flex h-11 w-11 items-center justify-center px-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-offset-0"
                                        >
                                            …
                                        </span>
                                    )
                                }

                                const isCurrentPage = pageNum === safePage
                                return (
                                    <button
                                        type="button"
                                        key={pageNum}
                                        onClick={() => handlePageClick(pageNum)}
                                        aria-disabled={loading}
                                        aria-current={isCurrentPage ? 'page' : undefined}
                                        aria-label={formatPageLabel(
                                            isCurrentPage ? mergedLabels.currentPageButton : mergedLabels.pageButton,
                                            pageNum
                                        )}
                                        className={cn(
                                            'btn-micro relative inline-flex h-11 items-center justify-center text-sm font-semibold focus:z-20',
                                            isCurrentPage
                                                ? 'z-10 w-14 bg-primary-600 px-4 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                                                : cn(
                                                    'w-11 px-2 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-offset-0',
                                                    loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                                )
                                        )}
                                    >
                                        {pageNum}
                                    </button>
                                )
                            })}

                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!hasNext}
                                aria-disabled={!hasNext || loading}
                                className={cn(
                                    'btn-micro relative inline-flex h-11 w-11 !min-h-[44px] items-center justify-center rounded-r-xl px-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:z-20 focus:outline-offset-0',
                                    hasNext && !loading
                                        ? 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                        : 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <span className="sr-only">{mergedLabels.next}</span>
                                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-5 w-5">
                                    <path
                                        d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                                        clipRule="evenodd"
                                        fillRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    )
}

Pagination.displayName = 'Pagination'
